import { useState, type DragEvent } from 'react'
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'

import { type PillOption } from '@/apps/latest/components/molecules/PillSelect'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { SectionHeading } from '@/components/atoms/SectionHeading'
import { RADIUS } from '@/shared/styles/radii'
import { ICON_SIZE } from '@/shared/styles/typography'
import { useToast } from '@/shared/contexts/ToastContext'

import { AttachmentAddButtons } from './AttachmentAddButtons'
import { AttachmentPreviewDialog } from './AttachmentPreviewDialog'
import { AttachmentRow, SendingRow } from './AttachmentRows'
import { noteSx, textActionSx } from './attachmentStyles'
import { type RecordingImageKind } from './recordingImageApi'
import { unreadAttachmentIds } from './unreadAttachments'
import { useAttachmentPicker } from './useAttachmentPicker'
import { useAttachmentSending } from './useAttachmentSending'
import { useRecordingImages } from './useRecordingImages'

interface Props {
  yakurekiId: string
  // 表示中の生成物が読めずに飛ばした資料の id
  skippedIds: string[]
  // 表示中の生成物が読んだ資料の id と、その名前 (SOAP・報告書)。読んだ id が null・未指定なら
  // 「まだ入っていない」を数えない (読んだ資料を残していない生成物を全部入っていないと言わない)
  readIds?: string[] | null
  documentName?: string
  // 「まだ入っていない」の印を釦にし、押すとその資料の id を渡す (差分更新を持つ系統 = 薬剤師)。
  // 持たない系統 (栄養) は渡さず、作り直すで読ませる
  onReflect?: (ids: string[]) => Promise<void>
  // 種別の一覧。渡したときだけ行に種別 (付けた後に任意で選ぶ)
  kinds?: PillOption<RecordingImageKind>[]
  // 足せない理由。渡すと足す口の代わりにこの 1 行を出す (既にある資料はそのまま並ぶ)
  blockedReason?: string
  // 完了後の節の見出し。'section' = 見出し「資料」(SOAP の節の見出しと同じ字格) の 2px 下に中身が開いたまま
  // (栄養の格子の下・薬剤師の SOAP カードの下の「AI で改善」の欄の 16px 下。hub log/108 決定 45・46。
  // 閉じた引き出しの行 = 決定 42・44 は同日夜に撤回)。省くとタブの中身 (見出しなし)
  heading?: 'section'
  // タブの中身 (固定の高さの枠): 枠の高さいっぱいに使い、一覧は内側で scroll、足す口は枠の下端に固定
  // (行の下に浮かせると、枠の空きの中で何にも揃わない。hub log/108 決定 39)
  fill?: boolean
  // 読むだけの置き場 (別の回の資料。管理栄養士の記録で回をさかのぼったとき)。足す口・✕・反映の印を持たず、
  // 行を押して開くだけ (design-book ADR-0076)
  readOnly?: boolean
}

const fillRootSx = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
} as const

const fillListSx = { flex: 1, minHeight: 0, overflow: 'auto' } as const

// 文の中の名前。英字の名前 (SOAP) だけ前後に空白を置く
const inText = (name: string) => (/^[A-Za-z]/.test(name) ? ` ${name} ` : name)

// 完了後の節の見出しの行。見出しは SOAP の節の見出し (S / O / A / フォローアップ) と同じ SectionHeading で、
// 箱も字下げも持たず左レールに置く (hub log/108 決定 46。決定 45 までの label・fg.default・高さ 30 の箱は、
// 消えた引き出しの行 = 決定 42 に合わせた服だった)。横に まだ入っていない資料の印 (4px)
const headingRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
} as const

// 見出しの横の印 (まだ入っていない資料がある) = 丸の中に ! (16px・fg.muted。色は付けず gentle な注意喚起)。
// 名前は tooltip と aria。✦ は採らない (すぐ上の AI で改善の行と同じ絵が縦に並び、「まだ」を言わない)。
// 未読ドットも採らない (「まだ」の注意を言う形ではない)
const unreadMarkIconSx = {
  fontSize: ICON_SIZE.md,
  color: colorTokens.fg.muted,
} as const

const unreadMarkButtonSx = {
  width: 24,
  height: 24,
  '&:hover': {
    bgcolor: colorTokens.bg.muted,
    '& svg': { color: colorTokens.fg.default },
  },
} as const

// 足す口の行。1 件 1 行と同じ高さで、絵の中心 x を行の先頭の 32px の絵 (サムネ・PDF) の中心に揃える
// (icon の釦は 28px に 16px の絵なので、2px ずらすと中心が 32px の絵の中心と同じ)。節でもタブでも同じ x (左レール)
const addRowSx = {
  display: 'flex',
  alignItems: 'center',
  minHeight: 32,
  pl: 0.25,
} as const

// 資料の一覧。1 件 1 行と注記と、その下の足す口 ([📷 撮る][⬆ 選ぶ] の icon 2 つ) を 1 つの箱に持つ
// (design-book 02-components/attachments-panel.md「資料の一覧」)。足す口は一覧の最後 (Notion の「+ New」と同じ位置)。
// 薬剤師は記録ブロックの添付資料タブ (録る前・録音中・生成中) と SOAP の下の「資料」の節 (完了後) の
// 2 つの置き場で同じ部品を使い、栄養は完了後の節に使う。
// 生成物が読んでいない資料があれば、見出しの横に静かな印 (丸の中に ! + tooltip「資料 N 件はまだ SOAP に入っていません」)。
// 差分更新を持つ系統は印を押すと反映する (hub log/108 決定 38。文の行は置かない)。
// サーバの一覧の全部を並べる (生成の後に足した資料も)。
export const AttachmentList = ({
  yakurekiId,
  skippedIds,
  readIds,
  documentName = 'SOAP',
  onReflect,
  kinds,
  blockedReason,
  heading,
  fill = false,
  readOnly = false,
}: Props) => {
  const { showToast } = useToast()
  const { list, changeKind, remove } = useRecordingImages(yakurekiId)
  const { sending, add, retry, save, reason, setReason } =
    useAttachmentSending(yakurekiId)
  const picker = useAttachmentPicker({
    onBlob: (blob) => add(blob),
    onUnreadable: setReason,
  })
  const [previewId, setPreviewId] = useState<string | null>(null)
  const [reflecting, setReflecting] = useState(false)
  const [dragging, setDragging] = useState(false)

  const images = list.data ?? []
  const preview = images.find((img) => img.id === previewId) ?? null

  // 消した資料の id は数えない (一覧に無い枚を「読めなかった」と言わない)
  const skippedCount = images.filter((img) =>
    skippedIds.includes(img.id)
  ).length
  // 生成の後に届いた・送り直した・完了後に足した資料。「反映する」か作り直すで消える
  const unread = unreadAttachmentIds(images, readIds, skippedIds)
  const notReadCount = unread?.length ?? 0
  const unreadLabel =
    notReadCount > 0
      ? `資料 ${notReadCount} 件はまだ${inText(documentName)}に入っていません`
      : null

  const handleReflect = async () => {
    if (!onReflect || !unread || reflecting) return
    setReason(null)
    setReflecting(true)
    try {
      await onReflect(unread)
    } catch {
      // 理由の 1 行は中身の下に出るので、閉じていれば開く
      setReason('反映できませんでした')
    } finally {
      setReflecting(false)
    }
  }

  // 足せる相だけ (読むだけの置き場は足せない)
  const canAdd = !blockedReason && !readOnly

  const openCamera = () => {
    setReason(null)
    picker.openCamera()
  }
  const openFilePicker = () => {
    setReason(null)
    picker.openFilePicker()
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (!canAdd) return
    e.preventDefault()
    setDragging(true)
  }
  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    // 子の要素へ移っただけのときは消さない
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
    setDragging(false)
  }
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    if (!canAdd) return
    e.preventDefault()
    setDragging(false)
    setReason(null)
    void picker.addFiles(Array.from(e.dataTransfer.files))
  }

  const addButtons = (
    <AttachmentAddButtons onCapture={openCamera} onPick={openFilePicker} />
  )

  // 見出しの横の印。差分更新を持つ系統は釦 (押すと反映)、持たない系統 (栄養) は印と tooltip だけ
  // (入れるのは系統の作り直す)
  const unreadMark =
    !unreadLabel || readOnly ? null : onReflect ? (
      <Tooltip
        title={
          reflecting ? '反映しています…' : `${unreadLabel}。押すと反映します`
        }
        placement="bottom"
      >
        <span>
          <IconButton
            size="small"
            aria-label={`${unreadLabel}。反映する`}
            disabled={reflecting}
            onClick={() => void handleReflect()}
            sx={unreadMarkButtonSx}
          >
            {reflecting ? (
              <CircularProgress
                size={12}
                sx={{ color: colorTokens.fg.muted }}
              />
            ) : (
              <ErrorOutlineOutlinedIcon sx={unreadMarkIconSx} />
            )}
          </IconButton>
        </span>
      </Tooltip>
    ) : (
      <Tooltip title={unreadLabel} placement="bottom">
        <Box
          component="span"
          role="img"
          aria-label={unreadLabel}
          sx={{ display: 'inline-flex' }}
        >
          <ErrorOutlineOutlinedIcon sx={unreadMarkIconSx} />
        </Box>
      </Tooltip>
    )

  const hasRows = images.length > 0 || sending.length > 0
  // 節 (見出しあり) は見出しの 2px 下から中身 (SOAP の見出しと行の間と同じ)。字下げはしない (見出しと同じ左レール)
  const listSx = {
    ...(fill ? fillListSx : {}),
    ...(heading ? { mt: 0.25 } : {}),
  }

  return (
    <Box
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        ...(fill ? fillRootSx : {}),
        borderRadius: `${RADIUS.md}px`,
        bgcolor: dragging ? colorTokens.bg.muted : 'transparent',
        transition: 'background-color 120ms',
      }}
    >
      {heading === 'section' && (
        <Box sx={headingRowSx}>
          <SectionHeading>資料</SectionHeading>
          {unreadMark}
        </Box>
      )}
      <Box sx={listSx}>
        {hasRows && (
          <Box>
            {images.map((img) => (
              <AttachmentRow
                key={img.id}
                image={img}
                kinds={kinds}
                onKindChange={(next) =>
                  changeKind.mutate(
                    { imageId: img.id, kind: next },
                    {
                      onError: () =>
                        showToast('種別を直せませんでした', 'error'),
                    }
                  )
                }
                onOpen={() => setPreviewId(img.id)}
                onDelete={
                  readOnly
                    ? undefined
                    : () =>
                        remove.mutate(img.id, {
                          onError: () =>
                            showToast('資料を削除できませんでした', 'error'),
                        })
                }
                deleting={remove.isPending && remove.variables === img.id}
              />
            ))}
            {sending.map((item) => (
              <SendingRow
                key={item.localId}
                item={item}
                onRetry={() => void retry(item)}
                onSave={() => save(item)}
              />
            ))}
          </Box>
        )}
        <Box>
          {readOnly && list.isSuccess && !hasRows && (
            // 読むだけの置き場は足す口も無いので、空のままだと読み込み中と見分けがつかない
            <Typography sx={noteSx}>この回の資料はありません</Typography>
          )}
          {list.isError && (
            <Typography sx={{ ...noteSx, mt: 0.5 }}>
              資料の一覧を読み込めませんでした{' '}
              <Box
                component="button"
                type="button"
                onClick={() => void list.refetch()}
                sx={textActionSx}
              >
                もう一度
              </Box>
            </Typography>
          )}
          {reason && (
            <Typography sx={{ ...noteSx, mt: 0.5 }}>{reason}</Typography>
          )}
          {skippedCount > 0 && (
            <Typography sx={{ ...noteSx, mt: 0.5 }}>
              資料 {skippedCount} 件を読めませんでした
            </Typography>
          )}
          {unreadLabel && !heading && (
            // 見出しの無い置き場 (印を置く行が無い) だけ文で言う
            <Typography sx={{ ...noteSx, mt: 0.5 }}>
              {unreadLabel}
              {onReflect && (
                <>
                  {' · '}
                  <Box
                    component="button"
                    type="button"
                    disabled={reflecting}
                    onClick={() => void handleReflect()}
                    sx={textActionSx}
                  >
                    {reflecting ? '反映しています…' : '反映する'}
                  </Box>
                </>
              )}
            </Typography>
          )}
        </Box>
      </Box>
      {/* 足す口は一覧の最後 (0 件ならこれだけ)。行と同じ高さ・同じ x。fill (タブ) では枠の下端に固定。
              紙の列の後ろでは 8px 空ける (紙と口は違う物。写真のサムネは箱いっぱいで icon は 16px なので、
              0 だと目に見える間が 8px で カードの下端までの 25px と非対称だった。hub log/108 決定 47)。
              足せない相はその理由の 1 行 */}
      {!readOnly && (
        <Box sx={{ ...addRowSx, mt: hasRows ? 1 : 0 }}>
          {blockedReason ? (
            <Typography sx={noteSx}>{blockedReason}</Typography>
          ) : (
            addButtons
          )}
        </Box>
      )}
      {!readOnly && picker.elements}
      <AttachmentPreviewDialog
        image={preview}
        onClose={() => setPreviewId(null)}
      />
    </Box>
  )
}
